import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * useGridNavigation — Hook pour la navigation matricielle au clavier dans les grilles et tableaux.
 * Conforme à la Note Technique AFT :
 * - Navigation verticale : Flèches Haut (↑) / Bas (↓)
 * - Navigation horizontale : Flèches Gauche (←) / Droite (→), ou Tab / Shift+Tab
 * - Validation de ligne : Entrée (↵)
 * - Ligne active : Surbrillance permanente
 * 
 * @param {Object} config - Configuration du tableau
 * @param {number} config.rowCount - Nombre de lignes
 * @param {number} config.colCount - Nombre de colonnes navigables
 * @param {Function} [config.onAddRow] - Callback appelé quand on valide la dernière ligne avec Entrée
 * @param {Function} [config.onReachEnd] - Callback quand on fait Tab depuis la toute dernière cellule
 * @param {Function} [config.onRowSelect] - Callback quand la ligne active change
 */
export const useGridNavigation = ({
  rowCount = 0,
  colCount = 0,
  onAddRow = null,
  onReachEnd = null,
  onRowSelect = null,
  containerRef = null
}) => {
  const [activeCoords, setActiveCoords] = useState({ row: 0, col: 0 });
  const [activeRowIdx, setActiveRowIdx] = useState(0);

  const rowCountRef = useRef(rowCount);
  const colCountRef = useRef(colCount);
  const onAddRowRef = useRef(onAddRow);
  const onReachEndRef = useRef(onReachEnd);
  const onRowSelectRef = useRef(onRowSelect);

  useEffect(() => {
    rowCountRef.current = rowCount;
    colCountRef.current = colCount;
    onAddRowRef.current = onAddRow;
    onReachEndRef.current = onReachEnd;
    onRowSelectRef.current = onRowSelect;
  }, [rowCount, colCount, onAddRow, onReachEnd, onRowSelect]);

  // Focus effectif de la cellule dans le DOM
  const focusCell = useCallback((row, col) => {
    const container = containerRef?.current || document;
    // Recherche par data-attributes
    const element = container.querySelector(`[data-row-idx="${row}"][data-col-idx="${col}"] input, [data-row-idx="${row}"][data-col-idx="${col}"] select, input[data-row-idx="${row}"][data-col-idx="${col}"], select[data-row-idx="${row}"][data-col-idx="${col}"]`);
    
    if (element) {
      element.focus();
      if (typeof element.select === 'function' && element.type !== 'date') {
        element.select();
      }
    }
  }, [containerRef]);

  const moveTo = useCallback((row, col) => {
    const maxRow = Math.max(0, rowCountRef.current - 1);
    const maxCol = Math.max(0, colCountRef.current - 1);

    const clampedRow = Math.max(0, Math.min(row, maxRow));
    const clampedCol = Math.max(0, Math.min(col, maxCol));

    setActiveCoords({ row: clampedRow, col: clampedCol });
    setActiveRowIdx(clampedRow);

    if (onRowSelectRef.current) {
      onRowSelectRef.current(clampedRow);
    }

    // Donner le focus au prochain tick pour laisser le state React se stabiliser
    setTimeout(() => {
      focusCell(clampedRow, clampedCol);
    }, 10);
  }, [focusCell]);

  const handleKeyDown = useCallback((e, currentRow, currentCol) => {
    const maxRow = Math.max(0, rowCountRef.current - 1);
    const maxCol = Math.max(0, colCountRef.current - 1);

    const r = currentRow !== undefined ? currentRow : activeCoords.row;
    const c = currentCol !== undefined ? currentCol : activeCoords.col;

    // 1. Déplacement vertical (Flèches Haut/Bas)
    if (e.key === 'ArrowUp') {
      // Ignorer si on est dans un dropdown ouvert (select/autocomplete)
      if (e.target.getAttribute('aria-expanded') === 'true') return;
      e.preventDefault();
      if (r > 0) {
        moveTo(r - 1, c);
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      if (e.target.getAttribute('aria-expanded') === 'true') return;
      e.preventDefault();
      if (r < maxRow) {
        moveTo(r + 1, c);
      }
      return;
    }

    // 2. Déplacement horizontal (Flèches Gauche/Droite)
    // Note: Déplacement uniquement si le curseur texte est au début/fin du champ ou si champ sélectionné
    if (e.key === 'ArrowLeft') {
      const input = e.target;
      const isAtStart = input.selectionStart === 0 && input.selectionEnd === 0;
      if (isAtStart || input.type === 'select-one' || input.readOnly) {
        if (c > 0) {
          e.preventDefault();
          moveTo(r, c - 1);
        } else if (r > 0) {
          e.preventDefault();
          moveTo(r - 1, maxCol);
        }
      }
      return;
    }

    if (e.key === 'ArrowRight') {
      const input = e.target;
      const isAtEnd = input.selectionStart === (input.value?.length || 0);
      if (isAtEnd || input.type === 'select-one' || input.readOnly) {
        if (c < maxCol) {
          e.preventDefault();
          moveTo(r, c + 1);
        } else if (r < maxRow) {
          e.preventDefault();
          moveTo(r + 1, 0);
        }
      }
      return;
    }

    // 3. Tabulation horizontale (Tab / Shift+Tab)
    if (e.key === 'Tab') {
      if (e.shiftKey) {
        // Shift + Tab : Recul
        if (c > 0) {
          e.preventDefault();
          moveTo(r, c - 1);
        } else if (r > 0) {
          e.preventDefault();
          moveTo(r - 1, maxCol);
        }
      } else {
        // Tab simple : Avancement
        if (c < maxCol) {
          e.preventDefault();
          moveTo(r, c + 1);
        } else if (r < maxRow) {
          e.preventDefault();
          moveTo(r + 1, 0);
        } else {
          // Dernière cellule de la dernière ligne : déclencher l'action de sortie (ex: focus bouton Valider)
          if (onReachEndRef.current) {
            onReachEndRef.current();
          }
        }
      }
      return;
    }

    // 4. Validation de ligne avec Entrée (↵)
    if (e.key === 'Enter') {
      // Ignorer si on est dans un menu autocomplete en train de choisir une option
      if (e.target.getAttribute('aria-expanded') === 'true') return;

      e.preventDefault();
      if (r < maxRow) {
        // Passer à la ligne suivante sur la première colonne
        moveTo(r + 1, 0);
      } else {
        // Dernière ligne : Ajouter une nouvelle ligne si callback présent
        if (onAddRowRef.current) {
          onAddRowRef.current();
          setTimeout(() => {
            moveTo(maxRow + 1, 0);
          }, 30);
        }
      }
    }
  }, [activeCoords, moveTo]);

  return {
    activeCoords,
    activeRowIdx,
    setActiveRowIdx,
    setActiveCoords,
    moveTo,
    handleKeyDown,
    focusCell
  };
};

export default useGridNavigation;
